/**
 * Accounting Reports Service
 * Trial Balance, P&L, Balance Sheet, Account Ledger, Sales Profit, Inventory Valuation
 *
 * SOURCE LOCK (Phase 1): All GL reports use only:
 * - COA: accounts
 * - Journal: journal_entries + journal_entry_lines
 * GL totals must not use retired duplicate subledger tables — journal + COA only.
 *
 * Canonical sale line table: sales_items; fallback: sale_items (legacy).
 */

import { supabase } from '@/lib/supabase';
import { inventoryService } from '@/app/services/inventoryService';
import { isCorrectionReversalReferenceType } from '@/app/lib/reportVisibilityContract';
import { fetchInBatches } from '@/app/lib/chunkInQuery';
import { COA_HEADER_CODES } from '@/app/data/defaultCoASeed';
import { assertGlTruthQueryTable } from '@/app/services/accountingCanonicalGuard';
import {
  buildAccountMapById,
  classifyBalanceSheetAsset,
  classifyBalanceSheetLiability,
  type BalanceSheetAssetGroup,
  type BalanceSheetLiabilityGroup,
} from '@/app/lib/accountHierarchy';
import { unifiedLedgerBranchIncludesRow } from '@/app/lib/unifiedLedgerBranchFilter';

/** Branch-scoped reports include JEs with NULL branch_id (company-wide openings and legacy rows). */
function journalEntryMatchesBranchFilter(
  jeBranchId: string | null | undefined,
  filterBranchId?: string | null
): boolean {
  if (!filterBranchId || filterBranchId === 'all') return true;
  if (jeBranchId == null || jeBranchId === '') return true;
  return jeBranchId === filterBranchId;
}

/** Fetch sale line items: sales_items first (canonical), fallback to sale_items for backward compatibility. */
async function getSaleLineItems<T = any>(selectColumns: string, saleIds: string[]): Promise<T[]> {
  const { data: fromSalesItems, error: err1 } = await supabase
    .from('sales_items')
    .select(selectColumns)
    .in('sale_id', saleIds);
  if (!err1 && fromSalesItems != null) return fromSalesItems as T[];
  const { data: fromSaleItems } = await supabase
    .from('sale_items')
    .select(selectColumns)
    .in('sale_id', saleIds);
  return (fromSaleItems || []) as T[];
}

export type TrialBalanceArApMode = 'flat' | 'summary' | 'expanded';

export interface TrialBalanceRow {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  debit: number;
  credit: number;
  balance: number; // debit positive, credit negative for display
  /** Indented child row under AR/AP control (expanded mode). */
  presentationIndent?: number;
  /** Canonical TB: number of underlying GL accounts rolled into this row */
  source_account_count?: number;
  /** Canonical TB: account ids for multi-account ledger drill */
  source_account_ids?: string[];
}

export interface TrialBalanceResult {
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
}

type AccountMetaRow = { id: string; code?: string; name?: string; type?: string; parent_id?: string | null };

function controlIdByCode(accounts: AccountMetaRow[], code: string): string | undefined {
  const c = code.trim();
  return accounts.find((a) => String(a.code || '').trim() === c)?.id;
}

function childIdsUnderParent(accounts: AccountMetaRow[], parentId: string | undefined): Set<string> {
  const s = new Set<string>();
  if (!parentId) return s;
  accounts.forEach((a) => {
    if (a.parent_id === parentId) s.add(a.id);
  });
  return s;
}

/** Summary: one row per AR/AP family (no double count in totals — same as flat). Expanded: group + indent party subledgers. */
function applyTrialBalanceArApPresentation(
  rows: TrialBalanceRow[],
  accounts: AccountMetaRow[],
  mode: TrialBalanceArApMode
): TrialBalanceRow[] {
  if (mode === 'flat' || !rows.length) return rows;

  const arControlId = controlIdByCode(accounts, '1100');
  const apControlId = controlIdByCode(accounts, '2000');
  const arChildren = childIdsUnderParent(accounts, arControlId);
  const apChildren = childIdsUnderParent(accounts, apControlId);

  const arFamily = new Set<string>([...(arControlId ? [arControlId] : []), ...arChildren]);
  const apFamily = new Set<string>([...(apControlId ? [apControlId] : []), ...apChildren]);

  if (mode === 'summary') {
    const rowMap = new Map(rows.map((r) => [r.account_id, r]));
    const sumFamily = (family: Set<string>, controlId: string | undefined): TrialBalanceRow | null => {
      let debit = 0;
      let credit = 0;
      family.forEach((id) => {
        const r = rowMap.get(id);
        if (r) {
          debit += r.debit;
          credit += r.credit;
        }
      });
      debit = Math.round(debit * 100) / 100;
      credit = Math.round(credit * 100) / 100;
      if (debit === 0 && credit === 0) return null;
      const meta = accounts.find((a) => a.id === controlId);
      if (!controlId || !meta) return null;
      return {
        account_id: controlId,
        account_code: meta.code || '',
        account_name: `${meta.name || ''} (subledger total)`,
        account_type: meta.type || '',
        debit,
        credit,
        balance: debit - credit,
      };
    }

    const rest = rows.filter((r) => !arFamily.has(r.account_id) && !apFamily.has(r.account_id));
    const arRoll = sumFamily(arFamily, arControlId);
    const apRoll = sumFamily(apFamily, apControlId);
    const out = [...rest];
    if (arRoll) out.push(arRoll);
    if (apRoll) out.push(apRoll);
    return out.sort((a, b) => (a.account_code || '').localeCompare(b.account_code || ''));
  }

  // expanded
  const rest = rows
    .filter((r) => !arFamily.has(r.account_id) && !apFamily.has(r.account_id))
    .map((r) => ({ ...r, presentationIndent: undefined }));

  const block = (family: Set<string>, controlId: string | undefined): TrialBalanceRow[] => {
    const slice = rows.filter((r) => family.has(r.account_id));
    if (!slice.length) return [];
    slice.sort((a, b) => {
      if (controlId) {
        if (a.account_id === controlId) return -1;
        if (b.account_id === controlId) return 1;
      }
      return (a.account_name || '').localeCompare(b.account_name || '');
    });
    return slice.map((r) => ({
      ...r,
      presentationIndent: controlId && r.account_id !== controlId ? 1 : 0,
    }));
  };

  const arB = block(arFamily, arControlId);
  const apB = block(apFamily, apControlId);
  const merged = [...rest.sort((a, b) => (a.account_code || '').localeCompare(b.account_code || '')), ...arB, ...apB];
  return merged;
}

export interface ProfitLossSection {
  label: string;
  items: { name: string; amount: number; code?: string }[];
  total: number;
}

export interface ProfitLossComparison {
  startDate: string;
  endDate: string;
  revenue: number;
  costOfSales: number;
  grossProfit: number;
  expenses: number;
  netProfit: number;
}

export interface ProfitLossResult {
  revenue: ProfitLossSection;
  costOfSales: ProfitLossSection;
  grossProfit: number;
  expenses: ProfitLossSection;
  netProfit: number;
  startDate: string;
  endDate: string;
  comparison?: ProfitLossComparison;
}

export interface BalanceSheetLineItem {
  name: string;
  amount: number;
  code?: string;
  /** Populated for assets: drives Cash & Bank vs Other grouping (parent chain + DB type). */
  bs_asset_group?: BalanceSheetAssetGroup;
  /** Populated for liabilities: trade vs payroll vs deposits (type + parent chain). */
  bs_liability_group?: BalanceSheetLiabilityGroup;
  /** Party drilldown in UI (1100 / 2000 control lines). */
  drilldownControl?: 'ar' | 'ap';
}

export interface BalanceSheetSection {
  label: string;
  items: BalanceSheetLineItem[];
  total: number;
}

export interface BalanceSheetResult {
  assets: BalanceSheetSection;
  liabilities: BalanceSheetSection;
  equity: BalanceSheetSection;
  totalAssets: number;
  totalLiabilitiesAndEquity: number;
  /** Assets − (Liabilities + Equity). After BS-FIX-01 absorption this equals tbImbalance. */
  difference: number;
  /** Raw Trial Balance imbalance = Σ(debit) − Σ(credit) across all posted journal lines.
   *  Non-zero means unbalanced journal entries exist; use Integrity Lab to find and fix. */
  tbImbalance: number;
  asOfDate: string;
}

export interface SalesProfitRow {
  sale_id: string;
  invoice_no: string;
  sale_date: string;
  customer_name: string;
  branch_id?: string | null;
  branch_name: string;
  revenue: number;
  cost: number;
  profit: number;
  margin_pct: number;
}

export interface SalesProfitResult {
  rows: SalesProfitRow[];
  totalRevenue: number;
  totalCost: number;
  totalProfit: number;
  startDate: string;
  endDate: string;
}

export interface InventoryValuationRow {
  product_id: string;
  variation_id?: string | null;
  product_name: string;
  sku: string;
  category: string;
  unit: string;
  quantity: number;
  unit_cost: number;
  total_value: number;
}

export interface InventoryValuationResult {
  rows: InventoryValuationRow[];
  totalValue: number;
  asOfDate: string;
}

// Normalize account type for grouping (DB may use asset, liability, revenue, expense, equity)
// P&L: Discount Allowed (5200) and Extra Expense (5300) are operating expenses — not cost of sales (see PF-06).
const REVENUE_TYPES = ['revenue', 'income'];
const EXPENSE_TYPES = ['expense', 'cost of sales', 'cogs'];
const ASSET_TYPES = ['asset', 'cash', 'bank', 'mobile_wallet', 'receivable', 'inventory'];
const LIABILITY_TYPES = ['liability', 'payable'];
const EQUITY_TYPES = ['equity'];
/** PF-06: Production/studio/shipping-to-fulfill — P&L Cost of Sales section. Excludes 5200/5300 (discount/extra). */
const COST_OF_PRODUCTION_CODES = new Set(['5000', '5010', '5100', '5110']);

function accountTypeCategory(type: string): 'revenue' | 'expense' | 'asset' | 'liability' | 'equity' {
  const t = (type || '').toLowerCase();
  if (REVENUE_TYPES.some((x) => t.includes(x))) return 'revenue';
  if (EXPENSE_TYPES.some((x) => t.includes(x))) return 'expense';
  if (ASSET_TYPES.some((x) => t.includes(x))) return 'asset';
  if (LIABILITY_TYPES.some((x) => t.includes(x))) return 'liability';
  if (EQUITY_TYPES.some((x) => t.includes(x))) return 'equity';
  return 'expense';
}

export const accountingReportsService = {
  /**
   * Phase 7: Account balances from journal only (single source of truth).
   * Returns account_id -> balance (debit - credit) for all accounts with activity up to asOfDate.
   * Use for Accounts screen and reconciliation; accounts with no journal activity map to balance 0 (do not use stored accounts.balance for GL UI).
   */
  async getAccountBalancesFromJournal(
    companyId: string,
    asOfDate?: string,
    branchId?: string
  ): Promise<Record<string, number>> {
    const end = (asOfDate ?? new Date().toISOString().slice(0, 10)).slice(0, 10);
    const mark = import.meta.env?.DEV ? `getAccountBalancesFromJournal:${companyId}` : '';
    if (mark) console.time(mark);
    try {
      const { data, error } = await supabase.rpc('get_account_balances_as_of', {
        p_company_id: companyId,
        p_as_of_date: end,
        p_branch_id: branchId ?? null,
      });
      if (!error && data && typeof data === 'object' && !Array.isArray(data)) {
        const out: Record<string, number> = {};
        for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
          out[k] = Number(v) || 0;
        }
        return out;
      }
      // Do NOT fall back to getTrialBalance('1900-01-01', …) — that storms journal_entry_lines
      // during hard-refresh boot and causes Day Book 57014 under From start.
      if (import.meta.env?.DEV) {
        console.warn(
          '[getAccountBalancesFromJournal] RPC unavailable — keeping stored balances (no client TB fallback):',
          error?.message ?? 'empty payload',
        );
      }
      return {};
    } catch (e) {
      if (import.meta.env?.DEV) {
        console.warn('[getAccountBalancesFromJournal] RPC error — keeping stored balances:', e);
      }
      return {};
    } finally {
      if (mark) console.timeEnd(mark);
    }
  },

  /**
   * Single TB pass for Contacts ↔ GL reconciliation (same branch scope as Contacts RPC).
   * - AR: TB row balance = debit − credit (asset; positive = usual).
   * - AP: compare contact payables to (credit − debit) on AP account = −balance.
   */
  async getArApGlSnapshot(
    companyId: string,
    asOfDate?: string,
    branchId?: string
  ): Promise<{
    ar: TrialBalanceRow | null;
    ap: TrialBalanceRow | null;
    /** Net liability on AP account (credits − debits), comparable to Contacts payables subtotal */
    apNetCredit: number | null;
    /** Worker payable control (2010) — separate from AP 2000 */
    wp: TrialBalanceRow | null;
    /** Net liability Cr − Dr on worker payable */
    wpNetCredit: number | null;
    /** Sum of Dr − Cr on cash / bank / mobile_wallet GL accounts (position, not flow) */
    cashBankNetDrMinusCr: number | null;
  }> {
    const end = (asOfDate ?? new Date().toISOString().slice(0, 10)).slice(0, 10);
    const tb = await this.getTrialBalance(companyId, '1900-01-01', end, branchId);
    const ar =
      tb.rows.find((r) => (r.account_code || '').trim() === '1100') ||
      tb.rows.find(
        (r) => /receivable/i.test(r.account_name || '') && accountTypeCategory(r.account_type) === 'asset'
      ) ||
      null;
    const ap =
      tb.rows.find((r) => (r.account_code || '').trim() === '2000') ||
      tb.rows.find(
        (r) =>
          /accounts payable/i.test((r.account_name || '').toLowerCase()) &&
          accountTypeCategory(r.account_type) === 'liability'
      ) ||
      null;
    const wp =
      tb.rows.find((r) => (r.account_code || '').trim() === '2010') ||
      tb.rows.find(
        (r) =>
          /worker.*payable/i.test((r.account_name || '').toLowerCase()) &&
          accountTypeCategory(r.account_type) === 'liability'
      ) ||
      null;
    const apNetCredit = ap ? ap.credit - ap.debit : null;
    const wpNetCredit = wp ? wp.credit - wp.debit : null;
    let cashBankNetDrMinusCr: number | null = null;
    const cashBankRows = tb.rows.filter((r) => {
      const t = (r.account_type || '').toLowerCase();
      return ['cash', 'bank', 'mobile_wallet'].some((k) => t.includes(k));
    });
    if (cashBankRows.length > 0) {
      cashBankNetDrMinusCr = cashBankRows.reduce((s, r) => s + (r.balance || 0), 0);
    }
    return { ar, ap, apNetCredit, wp, wpNetCredit, cashBankNetDrMinusCr };
  },

  /**
   * Trial Balance: SUM(debit), SUM(credit) per account for date range.
   * Join journal_entry_lines -> journal_entries (filter by company, date), join accounts.
   * When every journal entry is double-entry balanced, totalDebit = totalCredit and difference = 0.
   */
  async getTrialBalance(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string,
    options?: { arApMode?: TrialBalanceArApMode; branchFilterMode?: 'legacy' | 'strict' }
  ): Promise<TrialBalanceResult> {
    assertGlTruthQueryTable('accountingReportsService.getTrialBalance', 'accounts');
    assertGlTruthQueryTable('accountingReportsService.getTrialBalance', 'journal_entry_lines');
    let accountsQuery = supabase
      .from('accounts')
      .select('id, code, name, type, parent_id')
      .eq('company_id', companyId)
      .eq('is_active', true);
    let { data: accounts, error: accErr } = await accountsQuery;
    if (accErr && String(accErr.message || '').includes('parent_id')) {
      const retry = await supabase
        .from('accounts')
        .select('id, code, name, type')
        .eq('company_id', companyId)
        .eq('is_active', true);
      accounts = retry.data as any;
    }
    if (!accounts?.length) {
      return { rows: [], totalDebit: 0, totalCredit: 0, difference: 0 };
    }
    const accountIds = accounts.map((a: any) => a.id);
    const accountMap = new Map(accounts.map((a: any) => [a.id, a]));

    const lineSelect = `
        account_id,
        debit,
        credit,
        journal_entry:journal_entries(entry_date, company_id, branch_id, is_void, reference_type)
      `;
    let lines: any[] = [];
    let error: { message?: string } | null = null;
    try {
      lines = await fetchInBatches(accountIds, async (chunk) => {
        const { data, error: chunkErr } = await supabase
          .from('journal_entry_lines')
          .select(lineSelect)
          .in('account_id', chunk);
        if (chunkErr) throw chunkErr;
        return data || [];
      });
    } catch (e: any) {
      error = e;
      lines = [];
    }
    if (error || !lines?.length) {
      const rows: TrialBalanceRow[] = accounts.map((a: any) => ({
        account_id: a.id,
        account_code: a.code || '',
        account_name: a.name || '',
        account_type: a.type || '',
        debit: 0,
        credit: 0,
        balance: 0,
      }));
      return { rows, totalDebit: 0, totalCredit: 0, difference: 0 };
    }

    const start = startDate.slice(0, 10);
    const end = endDate.slice(0, 10);
    const byAccount: Record<string, { debit: number; credit: number }> = {};
    accountIds.forEach((id: string) => {
      byAccount[id] = { debit: 0, credit: 0 };
    });
    lines.forEach((line: any) => {
      const je = line.journal_entry;
      if (!je || je.company_id !== companyId) return;
      if ((je as any).is_void === true) return;
      const ed = (je.entry_date || '').slice(0, 10);
      if (ed < start || ed > end) return;
      const branchOk =
        options?.branchFilterMode === 'strict'
          ? unifiedLedgerBranchIncludesRow(branchId, {
              branchId: je.branch_id,
              referenceType: (je as { reference_type?: string }).reference_type,
            })
          : journalEntryMatchesBranchFilter(je.branch_id, branchId);
      if (!branchOk) return;
      const accId = line.account_id;
      if (!byAccount[accId]) byAccount[accId] = { debit: 0, credit: 0 };
      byAccount[accId].debit += Number(line.debit) || 0;
      byAccount[accId].credit += Number(line.credit) || 0;
    });

    // Issue 03: Use raw sums for totals so TB difference is 0 when data balances (avoid rounding drift)
    let rawTotalDebit = 0;
    let rawTotalCredit = 0;
    const rows: TrialBalanceRow[] = accounts
      .map((a: any) => {
        const d = byAccount[a.id] || { debit: 0, credit: 0 };
        rawTotalDebit += d.debit;
        rawTotalCredit += d.credit;
        const debit = Math.round(d.debit * 100) / 100;
        const credit = Math.round(d.credit * 100) / 100;
        const balance = debit - credit;
        return {
          account_id: a.id,
          account_code: a.code || '',
          account_name: a.name || '',
          account_type: a.type || '',
          debit,
          credit,
          balance,
        };
      })
      .filter((r) => r.debit !== 0 || r.credit !== 0)
      .sort((a, b) => (a.account_code || '').localeCompare(b.account_code || ''));

    const totalDebit = Math.round(rawTotalDebit * 100) / 100;
    const totalCredit = Math.round(rawTotalCredit * 100) / 100;
    const difference = Math.round((totalDebit - totalCredit) * 100) / 100;
    const mode = options?.arApMode || 'flat';
    const presented =
      mode === 'flat'
        ? rows
        : applyTrialBalanceArApPresentation(rows, (accounts || []) as AccountMetaRow[], mode);
    return { rows: presented, totalDebit, totalCredit, difference };
  },

  /**
   * Profit & Loss: Revenue - Cost of Sales = Gross Profit; Gross Profit - Expenses = Net Profit.
   * Uses journal_entry_lines joined to journal_entries (date filter), grouped by account type.
   * Optional comparison period: pass compareStartDate/compareEndDate to get prior-period totals.
   */
  async getProfitLoss(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string,
    options?: { compareStartDate?: string; compareEndDate?: string }
  ): Promise<ProfitLossResult> {
    const tb = await this.getTrialBalance(companyId, startDate, endDate, branchId);
    const revenueItems: { name: string; amount: number; code?: string }[] = [];
    const costItems: { name: string; amount: number; code?: string }[] = [];
    const expenseItems: { name: string; amount: number; code?: string }[] = [];

    let totalRevenue = 0;
    let totalCost = 0;
    let totalExpenses = 0;
    tb.rows.forEach((r) => {
      const cat = accountTypeCategory(r.account_type);
      const revenueAmount = cat === 'revenue' ? r.credit - r.debit : 0;
      const expenseAmount = cat === 'expense' ? r.debit - r.credit : 0;
      if (cat === 'revenue' && revenueAmount !== 0) {
        totalRevenue += revenueAmount;
        revenueItems.push({ name: r.account_name, amount: revenueAmount, code: r.account_code });
      } else if (cat === 'expense' && expenseAmount > 0) {
        const code = String(r.account_code ?? '').trim();
        const typeLower = (r.account_type || '').toLowerCase();
        const isCostOfProduction = COST_OF_PRODUCTION_CODES.has(code) || typeLower.includes('cogs') || typeLower.includes('cost');
        if (isCostOfProduction) {
          totalCost += expenseAmount;
          costItems.push({ name: r.account_name, amount: expenseAmount, code: r.account_code });
        } else {
          totalExpenses += expenseAmount;
          expenseItems.push({ name: r.account_name, amount: expenseAmount, code: r.account_code });
        }
      }
    });
    const grossProfit = totalRevenue - totalCost;
    const netProfit = grossProfit - totalExpenses;

    let comparison: ProfitLossComparison | undefined;
    if (options?.compareStartDate && options?.compareEndDate) {
      const compareTb = await this.getTrialBalance(companyId, options.compareStartDate, options.compareEndDate, branchId);
      let compRevenue = 0, compCost = 0, compExpenses = 0;
      compareTb.rows.forEach((r) => {
        const cat = accountTypeCategory(r.account_type);
        const revenueAmount = cat === 'revenue' ? r.credit - r.debit : 0;
        const expenseAmount = cat === 'expense' ? r.debit - r.credit : 0;
        if (cat === 'revenue') compRevenue += revenueAmount;
        else if (cat === 'expense' && expenseAmount > 0) {
          const code = String(r.account_code ?? '').trim();
          const typeLower = (r.account_type || '').toLowerCase();
          const isCostOfProduction = COST_OF_PRODUCTION_CODES.has(code) || typeLower.includes('cogs') || typeLower.includes('cost');
          if (isCostOfProduction) compCost += expenseAmount;
          else compExpenses += expenseAmount;
        }
      });
      comparison = {
        startDate: options.compareStartDate,
        endDate: options.compareEndDate,
        revenue: compRevenue,
        costOfSales: compCost,
        grossProfit: compRevenue - compCost,
        expenses: compExpenses,
        netProfit: compRevenue - compCost - compExpenses,
      };
    }

    return {
      revenue: { label: 'Revenue', items: revenueItems, total: totalRevenue },
      costOfSales: { label: 'Cost of Sales', items: costItems, total: totalCost },
      grossProfit,
      expenses: { label: 'Expenses', items: expenseItems, total: totalExpenses },
      netProfit,
      startDate,
      endDate,
      comparison,
    };
  },

  /**
   * Balance Sheet: Assets = Liabilities + Equity (as at a date).
   * Uses trial balance logic but for all time up to asOfDate; filter account types.
   * Issue 09: Include all asset/liability/equity accounts (including zero balance) so Inventory (1200) etc. appear.
   */
  async getBalanceSheet(
    companyId: string,
    asOfDate: string,
    branchId?: string
  ): Promise<BalanceSheetResult> {
    const start = '1900-01-01';
    const end = asOfDate.slice(0, 10);
    const tb = await this.getTrialBalance(companyId, start, end, branchId);

    const balanceByAccountId = new Map<string, number>();
    tb.rows.forEach((r) => balanceByAccountId.set(r.account_id, r.balance));

    const { data: accounts } = await supabase
      .from('accounts')
      .select('id, code, name, type, parent_id, is_group')
      .eq('company_id', companyId)
      .eq('is_active', true);

    const hierarchyRows = (accounts || []).map((a: any) => ({
      id: a.id,
      code: a.code,
      name: a.name,
      type: a.type,
      parent_id: a.parent_id,
    }));
    const accountMapForBs = buildAccountMapById(hierarchyRows);

    const arControl = (accounts || []).find((x: any) => String(x.code || '').trim() === '1100');
    const apControl = (accounts || []).find((x: any) => String(x.code || '').trim() === '2000');
    const arChildIds = new Set(
      (accounts || []).filter((x: any) => x.parent_id && arControl && x.parent_id === arControl.id).map((x: any) => x.id)
    );
    const apChildIds = new Set(
      (accounts || []).filter((x: any) => x.parent_id && apControl && x.parent_id === apControl.id).map((x: any) => x.id)
    );
    const rolledArBalance = (): number => {
      if (!arControl?.id) return 0;
      const ids = [arControl.id as string, ...arChildIds];
      return ids.reduce((s, id) => s + (balanceByAccountId.get(id) ?? 0), 0);
    };
    const rolledApBalance = (): number => {
      if (!apControl?.id) return 0;
      const ids = [apControl.id as string, ...apChildIds];
      return ids.reduce((s, id) => s + (balanceByAccountId.get(id) ?? 0), 0);
    };

    const assetItems: BalanceSheetLineItem[] = [];
    const liabilityItems: BalanceSheetLineItem[] = [];
    const equityItems: BalanceSheetLineItem[] = [];

    let totalAssets = 0;
    let totalLiabilities = 0;
    let totalEquity = 0;
    let revenueExpenseBalanceSum = 0;
    (accounts || []).forEach((a: any) => {
      const codeTrim = String(a.code || '').trim();
      if (COA_HEADER_CODES.has(codeTrim)) return;
      if (a.is_group === true) return;
      if (arChildIds.has(a.id) || apChildIds.has(a.id)) return;

      const cat = accountTypeCategory(a.type || '');
      let amount = balanceByAccountId.get(a.id) ?? 0;
      if (arControl && a.id === arControl.id) {
        amount = rolledArBalance();
      } else if (apControl && a.id === apControl.id) {
        amount = rolledApBalance();
      }
      if (cat === 'asset') {
        // Preserve actual sign: positive = normal debit-heavy asset (cash/bank/AR).
        // Negative = credit-heavy (bank overdraft, reversed AR) — shown as negative so BS reflects reality.
        // Math.abs was used before which hid overdrafts and broke Assets = Liabilities + Equity.
        const displayAmount = amount + 0; // +0 collapses IEEE-754 negative-zero to 0
        totalAssets += displayAmount;
        const bs_asset_group = classifyBalanceSheetAsset(
          {
            code: a.code,
            name: a.name,
            type: a.type,
            parent_id: a.parent_id,
          },
          accountMapForBs
        );
        assetItems.push({
          name: a.name || '',
          amount: displayAmount,
          code: a.code || '',
          bs_asset_group,
          drilldownControl: codeTrim === '1100' ? 'ar' : undefined,
        });
      } else if (cat === 'liability') {
        // Negate TB balance (Dr−Cr): normal credit-heavy liabilities have negative TB balance → positive in BS.
        // Debit-heavy liability (over-paid supplier etc.) has positive TB balance → negative in BS (flags anomaly).
        const displayAmount = (-amount) + 0;
        totalLiabilities += displayAmount;
        const bs_liability_group = classifyBalanceSheetLiability(
          {
            code: a.code,
            name: a.name,
            type: a.type,
            parent_id: a.parent_id,
          },
          accountMapForBs
        );
        liabilityItems.push({
          name: a.name || '',
          amount: displayAmount,
          code: a.code || '',
          bs_liability_group,
          drilldownControl: codeTrim === '2000' ? 'ap' : undefined,
        });
      } else if (cat === 'equity') {
        // Negate TB balance: normal credit-heavy equity → positive in BS.
        const displayAmount = (-amount) + 0;
        totalEquity += displayAmount;
        equityItems.push({ name: a.name || '', amount: displayAmount, code: a.code || '' });
      } else if (cat === 'revenue' || cat === 'expense') {
        revenueExpenseBalanceSum += amount;
      }
    });
    assetItems.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    liabilityItems.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    equityItems.sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    // ── PHASE BS-FIX-01: Absorb balances from excluded group/header accounts ──────────────────────
    // The main loop skips (a) COA header-code accounts and (b) is_group=true accounts.
    // If any journal lines were ever posted directly to those accounts (mis-posting, legacy data),
    // their balance lives in the Trial Balance but is invisible in the BS loop → causes
    // Assets ≠ Liabilities + Equity.
    //
    // Fix: after the line-item loop, iterate all TB rows. For any row whose account was NOT already
    // processed in the main loop (and is NOT an AR/AP child — those are already rolled up into their
    // control's amount), classify the balance and absorb it into the matching section total.
    // The balance is NOT added as a visible line item; it silently corrects the arithmetic.
    //
    // After absorption, the only remaining Difference = genuine Trial Balance imbalance
    // (totalDebit ≠ totalCredit in journal_entries), which is reported as `tbImbalance`.
    //
    // Proof: Difference = Σ(all included Dr-Cr) = Σ(all TB rows) − Σ(excluded after absorption)
    //                   = TB_imbalance − 0 = TB_imbalance.
    {
      const processedInMainLoop = new Set<string>();
      (accounts || []).forEach((a: any) => {
        const codeTrim = String(a.code || '').trim();
        if (COA_HEADER_CODES.has(codeTrim)) return;
        if (a.is_group === true) return;
        // AR/AP children are skipped from main loop but their balance IS absorbed via rolled control.
        // We must not add them again here.
        if (arChildIds.has(a.id) || apChildIds.has(a.id)) return;
        processedInMainLoop.add(a.id);
      });

      tb.rows.forEach((r) => {
        // Already counted in main loop
        if (processedInMainLoop.has(r.account_id)) return;
        // AR/AP children: included via rolled-up control account — skip to avoid double-count
        if (arChildIds.has(r.account_id) || apChildIds.has(r.account_id)) return;
        if (!r.balance) return; // zero — nothing to absorb
        const a = accountMapForBs.get(r.account_id) as any;
        if (!a) return;
        const cat = accountTypeCategory(a.type || '');
        if (cat === 'asset') {
          totalAssets += r.balance;
        } else if (cat === 'liability') {
          totalLiabilities += -(r.balance);
        } else if (cat === 'equity') {
          totalEquity += -(r.balance);
        } else {
          // revenue or expense: fold into P&L sum; netIncome is computed below
          revenueExpenseBalanceSum += r.balance;
        }
      });
    }

    // PF-04 / Issue 04: Include net income in equity so Assets = Liabilities + Equity.
    // Must be computed AFTER the absorption step above so revenueExpenseBalanceSum is complete.
    const netIncome = Math.round(-revenueExpenseBalanceSum * 100) / 100;
    if (netIncome !== 0) {
      totalEquity += netIncome;
      equityItems.push({ name: 'Net Income (to date)', amount: netIncome, code: '' });
    }

    // tbImbalance: after absorbing all reachable accounts, any remaining Assets − L+E gap
    // equals the raw TB imbalance (sum of all Dr-Cr across all journal_entry_lines).
    // A non-zero value means there are unbalanced journal entries — fixable via Integrity Lab.
    const tbImbalance = Math.round((tb.totalDebit - tb.totalCredit) * 100) / 100;

    const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;
    const difference = Math.round((totalAssets - totalLiabilitiesAndEquity) * 100) / 100;
    return {
      assets: { label: 'Assets', items: assetItems, total: totalAssets },
      liabilities: { label: 'Liabilities', items: liabilityItems, total: totalLiabilities },
      equity: { label: 'Owner Equity', items: equityItems, total: totalEquity },
      totalAssets,
      totalLiabilitiesAndEquity,
      difference,
      tbImbalance,
      asOfDate: end,
    };
  },

  /**
   * Sales Profit: per-sale revenue (canonical sale total), cost (from line items × product cost), profit.
   * Issue 07: Revenue always from sales.total (includes shipping/correct total); cost from sales_items/sale_items + product cost_price/cost.
   */
  async getSalesProfit(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string,
    customerId?: string
  ): Promise<SalesProfitResult> {
    let saleQuery = supabase
      .from('sales')
      .select('id, invoice_no, invoice_date, total, customer_id, customer:contacts(name), branch_id, branch:branches(id, name, code)')
      .eq('company_id', companyId)
      .eq('status', 'final')
      .gte('invoice_date', startDate.slice(0, 10))
      .lte('invoice_date', endDate.slice(0, 10));
    if (branchId) saleQuery = saleQuery.eq('branch_id', branchId);
    if (customerId) saleQuery = saleQuery.eq('customer_id', customerId);
    const { data: sales } = await saleQuery.order('invoice_date', { ascending: false });
    if (!sales?.length) {
      return { rows: [], totalRevenue: 0, totalCost: 0, totalProfit: 0, startDate, endDate };
    }
    const saleIds = sales.map((s: any) => s.id);
    const items = await getSaleLineItems('sale_id, quantity, unit_price, total, product_id, product:products(cost_price)', saleIds);
    const costBySale: Record<string, number> = {};
    saleIds.forEach((id) => {
      costBySale[id] = 0;
    });
    items.forEach((item: any) => {
      const saleId = item.sale_id;
      const costPerUnit = Number((item.product && item.product.cost_price) || 0) || 0;
      const cost = costPerUnit * (Number(item.quantity) || 0);
      costBySale[saleId] = (costBySale[saleId] || 0) + cost;
    });
    const rows: SalesProfitRow[] = sales.map((s: any) => {
      const revenue = Number(s.total) ?? 0;
      const cost = costBySale[s.id] ?? 0;
      const profit = revenue - cost;
      const margin_pct = revenue > 0 ? (profit / revenue) * 100 : 0;
      return {
        sale_id: s.id,
        invoice_no: s.invoice_no || `S-${s.id?.slice(0, 8)}`,
        sale_date: s.invoice_date || '',
        customer_name: s.customer?.name || '—',
        branch_id: s.branch_id ?? null,
        branch_name: s.branch?.name || s.branch?.code || '—',
        revenue,
        cost,
        profit,
        margin_pct,
      };
    });
    const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
    const totalCost = rows.reduce((s, r) => s + r.cost, 0);
    const totalProfit = totalRevenue - totalCost;
    return { rows, totalRevenue, totalCost, totalProfit, startDate, endDate };
  },

  /**
   * Inventory Valuation: canonical stock from inventoryService.getInventoryOverview (same names/SKU as Inventory screen).
   */
  async getInventoryValuation(
    companyId: string,
    asOfDate?: string,
    branchId?: string
  ): Promise<InventoryValuationResult> {
    const asOf = asOfDate ? asOfDate.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const overview = await inventoryService.getInventoryOverview(companyId, branchId);
    const rows: InventoryValuationRow[] = [];

    overview.forEach((product) => {
      const baseName = (product.name || '').trim() || '—';
      const baseSku = (product.sku || '').trim() || '—';
      const category = (product.category || '').trim() || '—';
      const unit = (product.unit || '').trim() || '—';

      if (product.hasVariations && product.variations?.length) {
        product.variations.forEach((v) => {
          if ((v.stock ?? 0) <= 0) return;
          const attrEntries =
            typeof v.attributes === 'object' && v.attributes !== null
              ? Object.entries(v.attributes).filter(([, val]) => String(val).trim() !== '')
              : [];
          const attrLabel = attrEntries.map(([, val]) => val).join(' / ');
          const displayName = attrLabel ? `${baseName} (${attrLabel})` : baseName;
          const sku = (v.sku || '').trim() || baseSku;
          const qty = v.stock ?? 0;
          const unitCost = v.purchasePrice ?? product.avgCost ?? 0;
          const totalValue = v.stockValueAtCost ?? qty * unitCost;
          rows.push({
            product_id: product.productId,
            variation_id: v.id,
            product_name: displayName,
            sku,
            category,
            unit,
            quantity: qty,
            unit_cost: Math.round(unitCost * 100) / 100,
            total_value: Math.round(totalValue * 100) / 100,
          });
        });
      } else if ((product.stock ?? 0) > 0) {
        const qty = product.stock ?? 0;
        const unitCost = product.avgCost ?? 0;
        const totalValue = product.stockValue ?? qty * unitCost;
        rows.push({
          product_id: product.productId,
          variation_id: null,
          product_name: baseName,
          sku: baseSku,
          category,
          unit,
          quantity: qty,
          unit_cost: Math.round(unitCost * 100) / 100,
          total_value: Math.round(totalValue * 100) / 100,
        });
      }
    });

    rows.sort((a, b) => a.product_name.localeCompare(b.product_name));
    const totalValue = Math.round(rows.reduce((s, r) => s + r.total_value, 0) * 100) / 100;
    return { rows, totalValue, asOfDate: asOf };
  },

  /**
   * Cash Flow Statement: Operating, Investing, Financing.
   * Uses journal_entries + journal_entry_lines; classifies by account types of the other leg.
   */
  async getCashFlowStatement(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string,
    options?: { auditMode?: boolean; basis?: 'official_gl' | 'effective_party' }
  ): Promise<{
    operating: { in: number; out: number; net: number };
    investing: { in: number; out: number; net: number };
    financing: { in: number; out: number; net: number };
    netChange: number;
    startDate: string;
    endDate: string;
  }> {
    const start = startDate.slice(0, 10);
    const end = endDate.slice(0, 10);
    const auditMode = options?.auditMode === true;
    const officialGlBasis = options?.basis === 'official_gl' || auditMode;
    const { data: accounts } = await supabase
      .from('accounts')
      .select('id, type')
      .eq('company_id', companyId)
      .eq('is_active', true);
    if (!accounts?.length) {
      return {
        operating: { in: 0, out: 0, net: 0 },
        investing: { in: 0, out: 0, net: 0 },
        financing: { in: 0, out: 0, net: 0 },
        netChange: 0,
        startDate: start,
        endDate: end,
      };
    }
    const cashBankIds = accounts
      .filter((a: any) => ['cash', 'bank', 'mobile_wallet'].includes(String(a.type).toLowerCase()))
      .map((a: any) => a.id);
    if (!cashBankIds.length) {
      return {
        operating: { in: 0, out: 0, net: 0 },
        investing: { in: 0, out: 0, net: 0 },
        financing: { in: 0, out: 0, net: 0 },
        netChange: 0,
        startDate: start,
        endDate: end,
      };
    }
    const { data: entriesInRange } = await supabase
      .from('journal_entries')
      .select('id, reference_type')
      .eq('company_id', companyId)
      .gte('entry_date', start)
      .lte('entry_date', end)
      .or('is_void.is.null,is_void.eq.false');
    const entryIdsInRange = (entriesInRange || [])
      .filter((e: { reference_type?: string | null }) =>
        officialGlBasis ? true : !isCorrectionReversalReferenceType(e.reference_type)
      )
      .map((e: { id: string }) => e.id);
    if (!entryIdsInRange.length) {
      return {
        operating: { in: 0, out: 0, net: 0 },
        investing: { in: 0, out: 0, net: 0 },
        financing: { in: 0, out: 0, net: 0 },
        netChange: 0,
        startDate: start,
        endDate: end,
      };
    }
    const cashLineSelect = `
        id, journal_entry_id, account_id, debit, credit,
        journal_entry:journal_entries(entry_date, company_id, branch_id),
        account:accounts(id, type)
      `;
    let cashLines: any[] = [];
    try {
      cashLines = await fetchInBatches(entryIdsInRange, async (chunk) => {
        const { data, error } = await supabase
          .from('journal_entry_lines')
          .select(cashLineSelect)
          .in('account_id', cashBankIds)
          .in('journal_entry_id', chunk);
        if (error) throw error;
        return data || [];
      }, { chunkSize: 20 });
    } catch {
      cashLines = [];
    }
    if (!cashLines.length) {
      return {
        operating: { in: 0, out: 0, net: 0 },
        investing: { in: 0, out: 0, net: 0 },
        financing: { in: 0, out: 0, net: 0 },
        netChange: 0,
        startDate: start,
        endDate: end,
      };
    }
    const entryIds = [...new Set(cashLines.map((l: any) => l.journal_entry_id))];
    let allLines: any[] = [];
    try {
      allLines = await fetchInBatches(entryIds, async (chunk) => {
        const { data, error } = await supabase
          .from('journal_entry_lines')
          .select('journal_entry_id, account_id, debit, credit, account:accounts(type)')
          .in('journal_entry_id', chunk);
        if (error) throw error;
        return data || [];
      });
    } catch {
      allLines = [];
    }
    const entryAccountTypes: Record<string, string[]> = {};
    (allLines || []).forEach((line: any) => {
      const jeId = line.journal_entry_id;
      if (!entryAccountTypes[jeId]) entryAccountTypes[jeId] = [];
      const t = (line.account?.type || '').toLowerCase();
      if (t && !entryAccountTypes[jeId].includes(t)) entryAccountTypes[jeId].push(t);
    });
    const operating = { in: 0, out: 0, net: 0 };
    const investing = { in: 0, out: 0, net: 0 };
    const financing = { in: 0, out: 0, net: 0 };
    cashLines.forEach((line: any) => {
      const je = line.journal_entry;
      if (!je || je.company_id !== companyId) return;
      if (!journalEntryMatchesBranchFilter(je.branch_id, branchId)) return;
      const types = entryAccountTypes[line.journal_entry_id] || [];
      const isRev = types.some((t) => REVENUE_TYPES.some((r) => t.includes(r)));
      const isExp = types.some((t) => EXPENSE_TYPES.some((e) => t.includes(e)));
      const isARAP = types.some((t) => t.includes('receivable') || t.includes('payable'));
      const isAsset = types.some((t) => ASSET_TYPES.some((a) => t.includes(a)) && !['cash', 'bank', 'mobile_wallet'].some((c) => t.includes(c)));
      const isLiqEq = types.some((t) => LIABILITY_TYPES.some((l) => t.includes(l)) || EQUITY_TYPES.some((e) => t.includes(e)));
      const debit = Number(line.debit) || 0;
      const credit = Number(line.credit) || 0;
      const cashIn = debit - credit;
      if (isRev || isARAP || isExp) {
        operating.in += cashIn > 0 ? cashIn : 0;
        operating.out += cashIn < 0 ? -cashIn : 0;
      } else if (isAsset) {
        investing.in += cashIn > 0 ? cashIn : 0;
        investing.out += cashIn < 0 ? -cashIn : 0;
      } else if (isLiqEq) {
        financing.in += cashIn > 0 ? cashIn : 0;
        financing.out += cashIn < 0 ? -cashIn : 0;
      } else {
        operating.in += cashIn > 0 ? cashIn : 0;
        operating.out += cashIn < 0 ? -cashIn : 0;
      }
    });
    operating.net = operating.in - operating.out;
    investing.net = investing.in - investing.out;
    financing.net = financing.in - financing.out;
    const netChange = operating.net + investing.net + financing.net;
    return {
      operating,
      investing,
      financing,
      netChange,
      startDate: start,
      endDate: end,
    };
  },

  /**
   * Profit by Product: aggregate at product level from sales_items (fallback sale_items).
   */
  async getProfitByProduct(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string
  ): Promise<{ product_id: string; product_name: string; sku: string; revenue: number; cost: number; profit: number; margin_pct: number }[]> {
    const saleQuery = supabase
      .from('sales')
      .select('id')
      .eq('company_id', companyId)
      .eq('status', 'final')
      .gte('invoice_date', startDate.slice(0, 10))
      .lte('invoice_date', endDate.slice(0, 10));
    if (branchId) (saleQuery as any).eq('branch_id', branchId);
    const { data: sales } = await saleQuery;
    if (!sales?.length) return [];
    const saleIds = sales.map((s: any) => s.id);
    const items = await getSaleLineItems('sale_id, product_id, quantity, unit_price, total, product:products(id, name, sku, cost_price, cost)', saleIds);
    const byProduct: Record<string, { revenue: number; cost: number }> = {};
    items.forEach((item: any) => {
      const pid = item.product_id || (item.product as any)?.id;
      if (!pid) return;
      if (!byProduct[pid]) byProduct[pid] = { revenue: 0, cost: 0 };
      const rev = Number(item.total) || Number(item.unit_price) * Number(item.quantity) || 0;
      const costPer = Number((item.product && (item.product.cost_price ?? item.product.cost)) || 0);
      byProduct[pid].revenue += rev;
      byProduct[pid].cost += costPer * (Number(item.quantity) || 0);
    });
    const productIds = Object.keys(byProduct);
    if (!productIds.length) return [];
    const { data: products } = await supabase.from('products').select('id, name, sku').in('id', productIds);
    const nameMap = new Map((products || []).map((p: any) => [p.id, { name: p.name, sku: p.sku }]));
    return productIds.map((pid) => {
      const r = byProduct[pid];
      const profit = r.revenue - r.cost;
      const margin_pct = r.revenue > 0 ? Math.round((profit / r.revenue) * 10000) / 100 : 0;
      const info = nameMap.get(pid);
      return {
        product_id: pid,
        product_name: info?.name || '—',
        sku: info?.sku || '—',
        revenue: Math.round(r.revenue * 100) / 100,
        cost: Math.round(r.cost * 100) / 100,
        profit: Math.round(profit * 100) / 100,
        margin_pct,
      };
    }).sort((a, b) => b.profit - a.profit);
  },

  /**
   * Profit by Category: same as product but grouped by product category (sales_items / sale_items fallback).
   */
  async getProfitByCategory(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string
  ): Promise<{ category_id: string; category_name: string; revenue: number; cost: number; profit: number; margin_pct: number }[]> {
    const saleQuery = supabase
      .from('sales')
      .select('id')
      .eq('company_id', companyId)
      .eq('status', 'final')
      .gte('invoice_date', startDate.slice(0, 10))
      .lte('invoice_date', endDate.slice(0, 10));
    if (branchId) (saleQuery as any).eq('branch_id', branchId);
    const { data: sales } = await saleQuery;
    if (!sales?.length) return [];
    const saleIds = sales.map((s: any) => s.id);
    const items = await getSaleLineItems('sale_id, product_id, quantity, unit_price, total, product:products(id, category_id, cost_price, cost, category:product_categories(id, name))', saleIds);
    const byCategory: Record<string, { revenue: number; cost: number; name: string }> = {};
    items.forEach((item: any) => {
      const cat = item.product?.category;
      const cid = cat?.id || 'uncategorized';
      const cname = cat?.name || 'Uncategorized';
      if (!byCategory[cid]) byCategory[cid] = { revenue: 0, cost: 0, name: cname };
      const rev = Number(item.total) || Number(item.unit_price) * Number(item.quantity) || 0;
      const costPer = Number((item.product && (item.product.cost_price ?? item.product.cost)) || 0);
      byCategory[cid].revenue += rev;
      byCategory[cid].cost += costPer * (Number(item.quantity) || 0);
    });
    return Object.entries(byCategory).map(([category_id, r]) => {
      const profit = r.revenue - r.cost;
      const margin_pct = r.revenue > 0 ? Math.round((profit / r.revenue) * 10000) / 100 : 0;
      return {
        category_id,
        category_name: r.name,
        revenue: Math.round(r.revenue * 100) / 100,
        cost: Math.round(r.cost * 100) / 100,
        profit: Math.round(profit * 100) / 100,
        margin_pct,
      };
    }).sort((a, b) => b.profit - a.profit);
  },

  /**
   * Profit by Customer: aggregate sales + sales_items (fallback sale_items) by customer_id.
   */
  async getProfitByCustomer(
    companyId: string,
    startDate: string,
    endDate: string,
    branchId?: string
  ): Promise<{ customer_id: string; customer_name: string; revenue: number; cost: number; profit: number; margin_pct: number }[]> {
    let saleQuery = supabase
      .from('sales')
      .select('id, customer_id, customer:contacts(id, name)')
      .eq('company_id', companyId)
      .eq('status', 'final')
      .gte('invoice_date', startDate.slice(0, 10))
      .lte('invoice_date', endDate.slice(0, 10));
    if (branchId) (saleQuery as any).eq('branch_id', branchId);
    const { data: sales } = await saleQuery;
    if (!sales?.length) return [];
    const saleIds = sales.map((s: any) => s.id);
    const items = await getSaleLineItems('sale_id, quantity, unit_price, total, product:products(cost_price, cost)', saleIds);
    const byCustomer: Record<string, { revenue: number; cost: number; name: string }> = {};
    items.forEach((item: any) => {
      const sale = sales.find((s: any) => s.id === item.sale_id);
      const cid = sale?.customer_id ?? 'walk-in';
      const cname = (sale?.customer as any)?.name || 'Walk-in';
      if (!byCustomer[cid]) byCustomer[cid] = { revenue: 0, cost: 0, name: cname };
      const rev = Number(item.total) || Number(item.unit_price) * Number(item.quantity) || 0;
      const costPer = Number((item.product && (item.product.cost_price ?? item.product.cost)) || 0);
      byCustomer[cid].revenue += rev;
      byCustomer[cid].cost += costPer * (Number(item.quantity) || 0);
    });
    return Object.entries(byCustomer).map(([customer_id, r]) => {
      const profit = r.revenue - r.cost;
      const margin_pct = r.revenue > 0 ? Math.round((profit / r.revenue) * 10000) / 100 : 0;
      return {
        customer_id,
        customer_name: r.name,
        revenue: Math.round(r.revenue * 100) / 100,
        cost: Math.round(r.cost * 100) / 100,
        profit: Math.round(profit * 100) / 100,
        margin_pct,
      };
    }).sort((a, b) => b.profit - a.profit);
  },
};
